import React, { useEffect, useMemo, useState } from 'react';
import { Timestamp, doc, setDoc } from 'firebase/firestore';
import { TikTokIcon } from '../icons/TikTokIcon';
import { InstagramIcon } from '../icons/InstagramIcon';
import { YouTubeIcon } from '../icons/YouTubeIcon';
import { db } from '../../../firebaseConfig.js';
import {
  getCanonicalSocials,
  getPrimarySocial,
  normalizePlatform,
  normalizeSocialInput,
  removeSocial,
  setPrimarySocial,
  upsertSocial,
} from '../../lib/socials';

const PLATFORM_OPTIONS = [
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'youtube', label: 'YouTube' },
];
// YouTube hidden from add flow until metrics API is available
const PLATFORM_OPTIONS_ADDABLE = PLATFORM_OPTIONS.filter((o) => o.value !== 'youtube');

const ICON_MAP = {
  tiktok: TikTokIcon,
  instagram: InstagramIcon,
  youtube: YouTubeIcon,
};

const domainIsValid = (platform, url) => {
  const value = url.toLowerCase();
  if (platform === 'tiktok') return value.includes('tiktok.com');
  if (platform === 'instagram') return value.includes('instagram.com');
  if (platform === 'youtube') return value.includes('youtube.com') || value.includes('youtu.be');
  return false;
};

const shortenUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace('www.', '') + parsed.pathname;
  } catch (error) {
    return url;
  }
};

const SocialMediaConnect = ({ userDoc, userId, onSocialsChange }) => {
  const [socials, setSocials] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [removeConfirmPlatform, setRemoveConfirmPlatform] = useState(null);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [platform, setPlatform] = useState('tiktok');
  const [handleOrUrl, setHandleOrUrl] = useState('');
  const [makePrimary, setMakePrimary] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSocials(getCanonicalSocials(userDoc));
  }, [userDoc]);

  const primarySocial = useMemo(() => getPrimarySocial(socials), [socials]);

  const openModal = (social = null) => {
    setError('');
    setEditingPlatform(social?.platform || null);
    setPlatform(social?.platform || 'tiktok');
    setHandleOrUrl(social?.url || (social?.handle ? `@${social.handle}` : ''));
    setMakePrimary(!!social?.isPrimary);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditingPlatform(null);
    setPlatform('tiktok');
    setHandleOrUrl('');
    setMakePrimary(false);
    setError('');
  };

  const persistSocials = async (nextSocials) => {
    // Mirror on influencers/{uid} so public /u/:handle pages work for guests (users/ is not readable for all accounts).
    await Promise.all([
      setDoc(doc(db, 'users', userId), { socials: nextSocials }, { merge: true }),
      setDoc(doc(db, 'influencers', userId), { socials: nextSocials }, { merge: true }),
    ]);
  };

  const handleSave = async () => {
    const normalizedPlatform = normalizePlatform(platform);
    if (!normalizedPlatform) {
      setError('Select a platform.');
      return;
    }

    if (!handleOrUrl.trim()) {
      setError('Handle or URL is required.');
      return;
    }

    const normalized = normalizeSocialInput(normalizedPlatform, handleOrUrl);
    if (!normalized.url || !domainIsValid(normalizedPlatform, normalized.url)) {
      setError(`Enter a valid ${normalizedPlatform} URL or handle.`);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const now = Timestamp.now();
      const existing = socials.find((social) => social.platform === normalizedPlatform);
      const nextSocial = {
        platform: normalizedPlatform,
        handle: normalized.handle,
        url: normalized.url,
        isPrimary: makePrimary || (!primarySocial && !editingPlatform),
        source: existing?.source || 'user',
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      };

      let nextSocials = upsertSocial(socials, nextSocial);
      if (nextSocial.isPrimary) {
        nextSocials = setPrimarySocial(nextSocials, normalizedPlatform);
      }

      if (!nextSocials.some((social) => social.isPrimary)) {
        nextSocials = setPrimarySocial(nextSocials, normalizedPlatform);
      }

      await persistSocials(nextSocials);
      setSocials(nextSocials);
      onSocialsChange?.(nextSocials);
      closeModal();
    } catch (err) {
      console.error('[SocialMediaConnect] Failed to save socials', err);
      setError('We could not save this social. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const openRemoveConfirm = (platformToRemove) => {
    setRemoveConfirmPlatform(platformToRemove);
  };

  const closeRemoveConfirm = () => {
    setRemoveConfirmPlatform(null);
  };

  const doRemoveSocial = async () => {
    const platformToRemove = removeConfirmPlatform;
    if (!platformToRemove) return;
    closeRemoveConfirm();
    const next = removeSocial(socials, platformToRemove);
    let normalized = next;
    if (next.length > 0 && !next.some((social) => social.isPrimary)) {
      normalized = setPrimarySocial(next, next[0].platform);
    }

    try {
      await persistSocials(normalized);
      setSocials(normalized);
      onSocialsChange?.(normalized);
    } catch (err) {
      console.error('[SocialMediaConnect] Failed to remove social', err);
      setError('We could not remove this social. Please try again.');
    }
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-3">
        {socials.map((social) => {
          const Icon = ICON_MAP[social.platform];
          return (
            <div
              key={social.platform}
              className="group relative inline-flex items-center rounded-full border border-slate-200 bg-white py-1 pr-8 pl-3 text-xs font-medium text-slate-700 transition"
            >
              <button
                type="button"
                onClick={() => openModal(social)}
                className="flex items-center gap-2 rounded-full text-left"
                aria-label={`Edit ${social.platform} social`}
              >
                {Icon ? <Icon className="h-4 w-4 flex-shrink-0 text-slate-700" /> : null}
                <span className="max-w-[180px] truncate">
                  {social.handle ? `@${social.handle}` : shortenUrl(social.url)}
                </span>
              </button>
              <button
                type="button"
                onClick={() => openRemoveConfirm(social.platform)}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[12px] leading-none text-slate-500 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-slate-200 hover:text-slate-700"
                aria-label={`Remove ${social.platform}`}
              >
                ×
              </button>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => openModal()}
          className="rounded-full border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-400"
        >
          + Social Media
        </button>
      </div>

      {socials.length === 0 && (
        <p className="mt-2 text-xs text-slate-500">
          Add a social profile so brands can find you.
        </p>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              {editingPlatform ? 'Edit social' : 'Add social'}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Add your primary platform so it shows on your profile.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Platform</label>
                <select
                  value={platform}
                  onChange={(event) => setPlatform(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                  disabled={!!editingPlatform}
                >
                  {PLATFORM_OPTIONS_ADDABLE.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Handle or URL</label>
                <input
                  type="text"
                  value={handleOrUrl}
                  onChange={(event) => setHandleOrUrl(event.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                  placeholder="@handle or https://..."
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={makePrimary}
                  onChange={(event) => setMakePrimary(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-[#1B9DA2]"
                />
                Make primary
              </label>

              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                  {error}
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="rounded-md bg-[#1B9DA2] px-4 py-2 text-sm font-semibold text-white hover:bg-[#168589] disabled:opacity-60"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove social – confirmation modal (onboarding-style popup) */}
      {removeConfirmPlatform && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">
              Are you sure?
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Are you sure you want to remove this social link? It will remove this social media&apos;s platform performance from your profile.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeRemoveConfirm}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doRemoveSocial}
                className="rounded-lg bg-[#1B9DA2] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#168589]"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialMediaConnect;
